import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ZODIAC_SIGNS } from "../../data/zodiacSigns";
import { ZODIAC_PIN_VH } from "../../zodiacConfig";

const DECOR_STARS = [
  { x: 120, y: 140, r: 1.8 },
  { x: 262, y: 112, r: 1.4 },
  { x: 302, y: 258, r: 2 },
  { x: 150, y: 288, r: 1.6 },
  { x: 232, y: 300, r: 1.3 },
  { x: 92, y: 222, r: 1.8 },
  { x: 312, y: 162, r: 1.4 },
  { x: 182, y: 122, r: 1.6 },
  { x: 262, y: 232, r: 1.3 },
  { x: 132, y: 250, r: 1.7 },
];

const DECOR_LINES = [
  [120, 140, 262, 112],
  [262, 232, 302, 258],
  [92, 222, 132, 250],
  [182, 122, 232, 300],
];

export const Zodiac = () => {
  const sectionRef = useRef(null);
  const wheelGroupRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!window.gsap || !window.ScrollTrigger || !sectionRef.current) return;
    const gsapLib = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    gsapLib.registerPlugin(ScrollTrigger);

    const STEPS = 11; // 11 intervals -> 12 discrete sign positions
    const pinDistance = window.innerHeight * ZODIAC_PIN_VH;
    const easeFn = gsapLib.parseEase("power2.inOut");
    let idleTimer = null;

    const applyVisual = (progress) => {
      const rotation = -progress * (STEPS / 12) * 360;
      gsapLib.set(wheelGroupRef.current, { rotation, transformOrigin: "50% 50%" });
      const k = Math.min(11, Math.max(0, Math.round(progress * STEPS)));
      setActiveIndex(k);
    };

    const st = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: "top top",
      end: `+=${pinDistance}`,
      pin: true,
      scrub: true,
      onUpdate: (self) => {
        applyVisual(self.progress);

        clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          const k = Math.round(self.progress * STEPS);
          const targetProgress = k / STEPS;
          if (Math.abs(self.progress - targetProgress) < 0.002) return;
          const targetY = self.start + targetProgress * (self.end - self.start);
          if (window.__lenis) {
            window.__lenis.scrollTo(targetY, { duration: 0.65, easing: easeFn, lock: true });
          } else {
            window.scrollTo({ top: targetY, behavior: "smooth" });
          }
        }, 90);
      },
    });

    return () => {
      clearTimeout(idleTimer);
      st.kill();
    };
  }, []);

  const sign = ZODIAC_SIGNS[activeIndex];

  return (
    <section id="zodiac" ref={sectionRef} className="relative min-h-screen w-full flex items-center overflow-hidden">
      <div className="cosmic-section w-full max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
        <div data-testid="zodiac-wheel" className="relative aspect-square w-full max-w-lg mx-auto">
          <svg viewBox="0 0 400 400" className="w-full h-full">
            <circle cx="200" cy="200" r="192" fill="none" stroke="#E8C27A" strokeWidth="2.5" opacity="0.65" />
            <circle cx="200" cy="200" r="150" fill="none" stroke="#A99BFF" strokeWidth="1" opacity="0.25" />

            {DECOR_LINES.map(([x1, y1, x2, y2], i) => (
              <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#A99BFF" strokeWidth="0.5" opacity="0.2" />
            ))}
            {DECOR_STARS.map((s, i) => (
              <circle key={i} cx={s.x} cy={s.y} r={s.r} fill="#f2eefb" opacity="0.4" />
            ))}

            <g ref={wheelGroupRef}>
              {ZODIAC_SIGNS.map((s, i) => {
                const angle = (i / 12) * Math.PI * 2 - Math.PI / 2;
                const x = 200 + 170 * Math.cos(angle);
                const y = 200 + 170 * Math.sin(angle);
                const lx = 200 + 192 * Math.cos(angle);
                const ly = 200 + 192 * Math.sin(angle);
                const isActive = i === activeIndex;
                return (
                  <g key={s.name}>
                    <line x1="200" y1="200" x2={lx} y2={ly} stroke="#A99BFF" strokeWidth="0.4" opacity="0.15" />
                    <text
                      data-testid={`zodiac-symbol-${s.name.toLowerCase()}`}
                      x={x}
                      y={y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="26"
                      fill={isActive ? "#E8C27A" : "#9b93c2"}
                      style={{
                        transformOrigin: `${x}px ${y}px`,
                        transform: isActive ? "scale(1.3)" : "scale(1)",
                        filter: isActive ? "drop-shadow(0 0 7px rgba(232,194,122,0.85))" : "none",
                        transition: "transform 0.45s cubic-bezier(0.22,1,0.36,1), fill 0.45s ease, filter 0.45s ease",
                      }}
                    >
                      {s.symbol}
                    </text>
                  </g>
                );
              })}
            </g>

            <polygon points="200,4 193,20 207,20" fill="#E8C27A" data-testid="zodiac-wheel-pointer" />
          </svg>
        </div>

        <div className="relative min-h-[280px] text-scrim">
          <p className="cosmic-eyebrow text-sm mb-5">Your Sky, Mapped</p>
          <AnimatePresence mode="wait">
            <motion.div
              key={sign.name}
              data-testid="zodiac-active-sign-panel"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -22 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              <p className="font-accent italic text-[#A99BFF] text-base mb-2">{sign.dates}</p>
              <h2 className="font-display text-4xl lg:text-5xl gold-gradient-text mb-6">{sign.name}</h2>
              <p className="text-lg text-[#F3EEE4] font-light leading-relaxed max-w-md">{sign.description}</p>
            </motion.div>
          </AnimatePresence>

          <div data-testid="zodiac-progress-indicator" className="flex items-center gap-4 mt-9">
            <span className="font-display text-sm tracking-[0.1em] text-[#A99BFF]">
              {String(activeIndex + 1).padStart(2, "0")} / 12
            </span>
            <div className="flex items-center gap-2">
              {ZODIAC_SIGNS.map((s, i) => (
                <span
                  key={s.name}
                  data-testid={`zodiac-progress-dot-${i}`}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === activeIndex ? "8px" : "5px",
                    height: i === activeIndex ? "8px" : "5px",
                    backgroundColor: i === activeIndex ? "#E8C27A" : "rgba(169,155,255,0.35)",
                    boxShadow: i === activeIndex ? "0 0 8px rgba(232,194,122,0.8)" : "none",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
